import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Records a short voice clip and sends it to the backend for multilingual
// transcription (Whisper). Calls onResult(query, data) with the search-ready
// English query. Works on Chrome/Edge/Android and iOS Safari (via MediaRecorder).
export const VoiceSearchButton = ({ onResult, className = "", title = "Search by voice" }) => {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const type = mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { setState("idle"); return; }
        setState("processing");
        try {
          const ext = type.includes("mp4") ? "mp4" : "webm";
          const fd = new FormData();
          fd.append("audio", blob, `voice.${ext}`);
          const { data } = await api.post("/ai/voice-search", fd);
          const query = (data.query || data.transcript || "").trim();
          if (!query) {
            toast.error("Didn't catch that — please try again");
          } else {
            onResult?.(query, data);
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Voice search failed");
        } finally {
          setState("idle");
        }
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch (e) {
      toast.error("Microphone access denied");
      setState("idle");
    }
  };

  const onClick = () => {
    if (state === "recording") stop();
    else if (state === "idle") start();
  };

  return (
    <Button
      type="button"
      data-testid="voice-search-btn"
      onClick={onClick}
      disabled={state === "processing"}
      variant="outline"
      title={state === "recording" ? "Tap to stop" : title}
      aria-label={title}
      className={`rounded-md ${state === "recording" ? "bg-[#C05A3C] text-white border-[#C05A3C] animate-pulse" : ""} ${className}`}
    >
      {state === "processing" ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : state === "recording" ? (
        <Square className="w-4 h-4" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </Button>
  );
};

export default VoiceSearchButton;
